import { Trans, useLingui } from '@lingui/react/macro'
import { LucideIcon } from 'components/LucideIcon'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { SidebarGroup, useSidebar } from 'designSystem/components/Sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { useCommandPalette } from 'features/commandPalette/contexts/CommandPaletteContext'
import { COMMAND_PALETTE_SHORTCUT_LABEL } from 'features/commandPalette/utils/shortcut'
import { useWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import styled from 'styled-components'

export function SidebarNewChatButton({
  siteId,
}: {
  siteId: string | undefined
}) {
  const { t } = useLingui()
  const { openNewAgenticChat } = useWorkspaceTabs()
  const { isEnabled: isSearchEnabled, openCommandPalette } = useCommandPalette()
  const { isMobile, state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const searchLabel = t`Search Cantilever (${COMMAND_PALETTE_SHORTCUT_LABEL})`

  return (
    <Box asChild flex='none'>
      <SidebarGroup>
        <Box
          display='flex'
          direction={isCollapsed ? 'column' : 'row'}
          align={isCollapsed ? 'center' : 'stretch'}
          gap='sm'>
          <Tooltip>
            <TooltipTrigger asChild>
              <Box asChild flex={isCollapsed ? 'none' : 'one'} minWidth='none'>
                <Button
                  gap={isCollapsed ? 'none' : 'default'}
                  disabled={!siteId}
                  width='full'
                  type='button'
                  variant='outline'
                  onClick={() => openNewAgenticChat()}>
                  <LucideIcon aria-hidden='true' name='Plus' />
                  <NewChatLabel>
                    <Trans>New chat</Trans>
                  </NewChatLabel>
                </Button>
              </Box>
            </TooltipTrigger>
            <TooltipContent
              align='center'
              hidden={!isCollapsed || isMobile}
              side='right'>
              {t`New chat`}
            </TooltipContent>
          </Tooltip>
          {isSearchEnabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Box asChild flex='none'>
                  <Button
                    aria-label={searchLabel}
                    size='icon'
                    type='button'
                    variant='outline'
                    onClick={openCommandPalette}>
                    <LucideIcon aria-hidden='true' name='Search' />
                  </Button>
                </Box>
              </TooltipTrigger>
              <TooltipContent
                align='center'
                hidden={isMobile}
                side={isCollapsed ? 'right' : 'bottom'}>
                {searchLabel}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </Box>
      </SidebarGroup>
    </Box>
  )
}

const NewChatLabel = styled.span`
  max-width: 10rem;
  overflow: hidden;
  transition:
    opacity 150ms var(--ease-out-strong),
    max-width 200ms var(--ease-out-strong);

  [data-collapsible='icon'] & {
    max-width: 0;
    opacity: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 150ms var(--ease-out-strong);
  }
`
